import { createContext, useContext, useEffect, useState } from 'react';
import api from '@/lib/api';

const SettingsContext = createContext(null);

export const DEFAULT_APPEARANCE = {
  font_family: 'Figtree',
  font_size_base: 16,
  background_color: '#F1F8E9',
  surface_color: '#FFFFFF',
  text_color: '#1B1B1B',
  muted_text_color: '#6B7280',
  primary_color: '#0F8A43',
  primary_hover_color: '#0B6932',
  accent_color: '#FF6B00',
  button_text_color: '#FFFFFF',
  button_radius: 24,
  button_hover_color: '#0B6932',
  card_radius: 24,
  card_shadow: 'medium',
  product_image_radius: 16,
  product_image_size: 'medium',
  grid_gap: 16,
  section_padding: 32,
  banner_height: 40,
  header_height: 64,
  sticky_header: true,
  search_bg: '#F5F5F4',
  search_border: '#E7E5E4',
  search_radius: 999,
  search_placeholder_color: '#A8A29E',
  navbar_bg: '#FFFFFF',
  navbar_icon_color: '#1B1B1B',
  bottom_nav_bg: '#FFFFFF',
  bottom_nav_icon_color: '#1B1B1B',
  footer_bg: '#1C1917',
  footer_text_color: '#D6D3D1',
  whatsapp_button_color: '#25D366',
  whatsapp_position: 'bottom-right',
  border_color: '#E7E5E4',
  badge_color: '#FF6B00',
  animations_enabled: true,
  animation_speed: 'normal',
  theme_mode: 'light',
};

export const DEFAULT_RUNNING_BANNER = {
  enabled: false,
  style: 'marquee',
  text: 'Free delivery on orders above ₹499 · Cash on Delivery available',
  bg_color: '#0F8A43',
  text_color: '#FFFFFF',
  speed: 30,
  dismissible: true,
};

export const DEFAULT_HERO_BANNER = {
  enabled: false,
  image_url: '',
  image_public_id: '',
  title: '',
  subtitle: '',
  button_text: '',
  button_link: '',
};

export const DEFAULT_SALE_TIMER = {
  enabled: false,
  start_iso: '',
  end_iso: '',
  title: 'Sale ends in',
};

const DEFAULTS = {
  delivery_charge: 30,
  min_order_value: 199,
  free_delivery_above: 499,
  store_name: 'MyZippyKart',
  store_phone: '+91 9971236105',
  whatsapp_number: '919971236105',
  is_store_open: true,
  delivery_time_text: 'Delivery slots — Morning: 5:00 AM – 8:30 AM | Evening: 7:00 PM – 10:30 PM',
  platform_fee: 0,
  service_area_label: 'Faridabad',
  serviceable_pincodes: [],
  banner_enabled: true,
  banner_text: 'Fresh groceries delivered. Cash on Delivery available.',
  offer_text: 'Flat ₹50 OFF on your first order — use code FRESH50',
  logo_url: '',
  primary_mobile: '9971236105',
  secondary_mobile: '',
  upi_id: '',
  upi_qr_url: '',
  appearance: DEFAULT_APPEARANCE,
  running_banner: DEFAULT_RUNNING_BANNER,
  hero_banner: DEFAULT_HERO_BANNER,
  sale_timer: DEFAULT_SALE_TIMER,
  content_version: 1,
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get('/settings');
      setSettings({
        ...DEFAULTS,
        ...data,
        appearance: { ...DEFAULT_APPEARANCE, ...(data.appearance || {}) },
        running_banner: { ...DEFAULT_RUNNING_BANNER, ...(data.running_banner || {}) },
        hero_banner: { ...DEFAULT_HERO_BANNER, ...(data.hero_banner || {}) },
        sale_timer: { ...DEFAULT_SALE_TIMER, ...(data.sale_timer || {}) },
      });
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  // Auto-refresh: poll cheap /settings/version every 15s; refresh full settings when it changes.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get('/settings/version');
        if (!cancelled && data && data.content_version !== settings.content_version) {
          refresh();
        }
      } catch (e) { /* ignore */ }
    };
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [settings.content_version]);

  return (
    <SettingsContext.Provider value={{ settings, loading, refresh, setSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
